import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { providersService } from '../../shared/services/providers.service';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';

export function EditBusinessNameDialog({
  open,
  initialName,
  onClose,
}: {
  open: boolean;
  initialName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const save = useMutation({
    mutationFn: () => providersService.updateMine({ businessName: name.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provider', 'me'] });
      notify.success(t('common.save'));
      onClose();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const trimmed = name.trim();
  const isValid = trimmed.length >= 2 && trimmed.length <= 160;
  const isUnchanged = trimmed === initialName.trim();

  const handleClose = () => {
    if (save.isPending) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="span">
          {t('provider.editNameTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('provider.editNameHint')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('provider.businessName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
            inputProps={{ maxLength: 160 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={save.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid || isUnchanged || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
